import React, { useEffect, useRef, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { getDashboardData, getDashboardSearchCases } from "../../redux/actions/DashboardActions";
import { Row, Col, Card, Table, Badge, Modal, Button, Spinner } from "react-bootstrap";
import PageTitle from "../../Components/PageTitle";
import { useNavigate } from "react-router-dom";
import { logout } from "../../redux/actions/AuthActions";
import StatisticsWidget from "../../Components/StatisticsWidget";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
} from "chart.js";
import { Pie, Bar, Doughnut } from "react-chartjs-2";
import "../../css/Dashboard.css";
ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement);

// Modern Dashboard Styles
const dashboardStyles = {
  card: {
    border: "none",
    borderRadius: "12px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    transition: "all 0.3s ease",
    overflow: "hidden",
    backgroundColor: "#ffffff"
  },
  cardHover: {
    boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
    transform: "translateY(-2px)"
  },
  cardHeader: {
    fontSize: "18px",
    fontWeight: "600",
    color: "#2c3e50",
    marginBottom: "20px",
    paddingBottom: "12px",
    borderBottom: "2px solid #f0f4f8",
    display: "flex",
    alignItems: "center",
    gap: "8px"
  },
  tableContainer: {
    borderRadius: "8px",
    overflow: "hidden",
    border: "1px solid #e8ecef",
    backgroundColor: "#ffffff"
  },
  tableHeader: {
    backgroundColor: "#f8f9fa",
    fontWeight: "600",
    color: "#495057",
    fontSize: "13px",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    borderBottom: "2px solid #dee2e6"
  },
  tableRow: {
    transition: "all 0.2s ease",
    borderBottom: "1px solid #f0f0f0"
  },
  tableRowHover: {
    backgroundColor: "#f8f9fa",
    cursor: "pointer"
  },
  badge: {
    padding: "6px 12px",
    borderRadius: "6px",
    fontWeight: "500",
    fontSize: "12px"
  },
  sectionTitle: {
    fontSize: "24px",
    fontWeight: "700",
    color: "#1a202c",
    marginBottom: "24px",
    paddingLeft: "4px"
  },
  statCard: {
    border: "none",
    borderRadius: "16px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    overflow: "hidden",
    backgroundColor: "#ffffff",
    position: "relative",
    height: "100%"
  },
  statCardHover: {
    boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
    transform: "translateY(-4px)"
  },
  statCardGradient: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "4px",
    background: "linear-gradient(90deg, #667eea 0%, #764ba2 100%)"
  }
};


const Dashboard = () => {

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    AuthReducer: { auth },
    dashboardReducer: { dashboardData, monthlyReportList, loadingMonthlyReport }
  } = useSelector((state) => state);

  const authToken = auth?.token;

  const [showMonthlyModal, setShowMonthlyModal] = useState(false);
  const [monthlyReportType, setMonthlyReportType] = useState(""); // "Active" | "Complete"
  const [searchContext, setSearchContext] = useState("monthly"); // "monthly" | "claimType" | "fo_wise" | "city_wise" | "hospital_wise" | "insurance_wise"
  const [selectedClaimTypeName, setSelectedClaimTypeName] = useState("");
  const [searchModalTitle, setSearchModalTitle] = useState(""); // Row label for FO/City/Hospital/Insurance

  // Refs to keep header width in sync with scrollbars on body
  const expiryHeaderRef = useRef(null);
  const expiryBodyRef = useRef(null);
  const foHeaderRef = useRef(null);
  const foBodyRef = useRef(null);
  const cityHeaderRef = useRef(null);
  const cityBodyRef = useRef(null);
  const hospitalHeaderRef = useRef(null);
  const hospitalBodyRef = useRef(null);
  const insuranceHeaderRef = useRef(null);
  const insuranceBodyRef = useRef(null);
  const completedHeaderRef = useRef(null);
  const completedBodyRef = useRef(null);
  const claimTypeChartRef = useRef(null);
  // Helper function to get count by case status
  const getStatusCount = (statusName) => {
    if (!dashboardData?.data || !Array.isArray(dashboardData.data)) return 0;
    const status = dashboardData.data.find(item => item.case_status === statusName);
    return status ? status.total : 0;
  };

  // Memoized pie chart data
  const monthlyCaseData = useMemo(() => {
    const active = dashboardData?.activeVsClosedCase?.active || 0;
    const closed = dashboardData?.activeVsClosedCase?.closed || 0;
    return {
      labels: ["Active Cases", "Completed Cases"],
      datasets: [
        {
          label: "Monthly Cases",
          data: [active, closed],
          backgroundColor: [
            "rgba(255, 107, 157, 0.9)",
            "rgba(79, 172, 254, 0.9)"
          ],
          borderColor: [
            "#ffffff",
            "#ffffff"
          ],
          borderWidth: 3,
          hoverBackgroundColor: [
            "rgba(255, 107, 157, 1)",
            "rgba(79, 172, 254, 1)"
          ],
        },
      ],
    };
  }, [dashboardData?.activeVsClosedCase]);

  // Case status distribution (bar)
  const statusBarData = useMemo(() => {
    const items = Array.isArray(dashboardData?.data) ? dashboardData.data : [];
    const labels = items.map((i) => i.case_status);
    const values = items.map((i) => i.total);
    return {
      labels,
      datasets: [
        {
          label: "Cases",
          data: values,
          backgroundColor: "rgba(102, 126, 234, 0.7)",
          borderColor: "#667eea",
          borderWidth: 1,
          borderRadius: 6,
        },
      ],
    };
  }, [dashboardData?.data]);

  const statusBarOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { mode: "index", intersect: false },
    },
    scales: {
      x: { grid: { display: false }, ticks: { color: "#64748b", font: { size: 12 } } },
      y: { beginAtZero: true, grid: { color: "#eef2f7" }, ticks: { color: "#64748b", stepSize: 1 } },
    },
  }), []);

  // Claim type split (doughnut)
  const claimTypeData = useMemo(() => {
    const items = Array.isArray(dashboardData?.claimTypeCount) ? dashboardData.claimTypeCount : [];
    const labels = items.map((i) => i.claim_type_name);
    const values = items.map((i) => i.total);
    const colors = ["#4facfe", "#ff6b9d", "#36a2eb", "#ffce56", "#4bc0c0", "#9966ff"]; // cycle if needed
    return {
      labels,
      datasets: [
        {
          data: values,
          backgroundColor: labels.map((_, idx) => colors[idx % colors.length]).map((c) => `${c}cc`),
          borderColor: "#ffffff",
          borderWidth: 3,
          hoverOffset: 6,
        },
      ],
    };
  }, [dashboardData?.claimTypeCount]);

  // City wise totals (horizontal bar)
  const cityBarData = useMemo(() => {
    const items = Array.isArray(dashboardData?.cityCount) ? dashboardData.cityCount : [];
    const labels = items.map((i) => i.city_name);
    const values = items.map((i) => i.total);
    return {
      labels,
      datasets: [
        {
          label: "Total",
          data: values,
          backgroundColor: "rgba(79, 172, 254, 0.7)",
          borderColor: "#4facfe",
          borderWidth: 1,
          borderRadius: 6,
        },
      ],
    };
  }, [dashboardData?.cityCount]);

  const cityBarOptions = useMemo(() => ({
    indexAxis: "y",
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: {
      x: { beginAtZero: true, grid: { color: "#eef2f7" }, ticks: { color: "#64748b", stepSize: 1 } },
      y: { grid: { display: false }, ticks: { color: "#64748b" } },
    },
  }), []);

  // Map menu keys to dashboard case_status values
  const keyToStatus = useMemo(() => ({
    case_in: "Initiated",
    case_generated: "Generated",
    case_assign_fo: "Assigned Field Officer",
    case_accepted_fo: "Case Accept By FO",
    case_rejected_fo: "Case Reject By FO",
    case_field_qc_completed: "Field Q.C. Pending",
    case_waiting_doctor: "Waiting for Doctor Allocation",
    case_doctor_report_pending: "Doctor Report Pending",
    case_manager_qc_pending: "Manager Q.C. Pending",
    case_team_leader_qc_pending: "Team Leader QC Pending",
    case_waiting_closer: "Waiting For Closer",
    case_closed: "Case Closed",
  }), []);

  // Extract permitted menu children for Active case
  const permittedMenuChildren = useMemo(() => {
    const activeMenu = dashboardData?.menuData?.find((m) => m?.key === "active_case");
    const children = activeMenu?.children || [];
    return children.filter((c) => Array.isArray(c.permissions) && c.permissions.some((p) => p?.view));
  }, [dashboardData?.menuData]);

  // Simple variant and icon rotation
  const widgetVariants = ["primary", "info", "success", "warning"];
  const widgetIcons = ["fe-heart", "fe-bar-chart-line-", "fe-shopping-cart", "fe-eye"];

  // If user lands on dashboard without token, send them to login
  useEffect(() => {
    if (!authToken) {
      navigate("/login", { replace: true });
    }
  }, [authToken, navigate]);

  useEffect(() => {
    if (authToken) {
      dispatch(getDashboardData(authToken));
    }
  }, [authToken, dispatch]);

  useEffect(() => {
    document.title = `Dashboard :: ${auth?.generalSettings?.title || ""}`;
  }, [auth?.generalSettings?.title]);

  const openClaimTypeCasesByIndex = (index) => {
    if (!authToken) return;
    const items = Array.isArray(dashboardData?.claimTypeCount) ? dashboardData.claimTypeCount : [];
    const claimItem = items[index];
    if (!claimItem) return;

    // Backend may send different id keys, try common ones
    const claimTypeId =
      claimItem.id ??
      claimItem.claim_type_id ??
      claimItem.claimTypeId ??
      claimItem.claim_typeId;
    if (!claimTypeId) return;

    setSearchContext("claimType");
    setSelectedClaimTypeName(claimItem.claim_type_name || "Claim Type");
    setShowMonthlyModal(true);
    dispatch(getDashboardSearchCases(authToken, "claim_type_split", claimTypeId));
  };

  const handleMonthlyCardClick = (value) => {
    if (!authToken) return;
    setSearchContext("monthly");
    setMonthlyReportType(value);
    setShowMonthlyModal(true);
    dispatch(getDashboardSearchCases(authToken, "monthly_report", value));
  };

  const openDashboardSearchByType = (type, valueId, title, claimTypeId) => {
    if (!authToken || valueId == null || valueId === "") return;
    setSearchContext(type);
    setSearchModalTitle(title || "");
    setShowMonthlyModal(true);
    dispatch(getDashboardSearchCases(authToken, type, valueId, claimTypeId));
  };

  const handleClaimTypeChartClick = (elements) => {
    if (!elements || !elements.length) return;

    const index = elements[0].index;
    openClaimTypeCasesByIndex(index);
  };


  // Keep header padding equal to scrollbar width of the scrollable body
  useEffect(() => {
    const pairs = [
      [expiryHeaderRef, expiryBodyRef],
      [foHeaderRef, foBodyRef],
      [cityHeaderRef, cityBodyRef],
      [hospitalHeaderRef, hospitalBodyRef],
      [insuranceHeaderRef, insuranceBodyRef],
      [completedHeaderRef, completedBodyRef],
    ];

    const sync = () => {
      pairs.forEach(([headerRef, bodyRef]) => {
        const headerEl = headerRef.current;
        const bodyEl = bodyRef.current;
        if (!headerEl || !bodyEl) return;
        const scrollbarWidth = bodyEl.offsetWidth - bodyEl.clientWidth;
        headerEl.style.paddingRight = `${Math.max(0, scrollbarWidth)}px`;
      });
    };

    // Initial sync after paint
    const id = requestAnimationFrame(sync);
    // Sync on resize
    window.addEventListener('resize', sync);

    return () => {
      cancelAnimationFrame(id);
      window.removeEventListener('resize', sync);
    };
  }, []);

  // setMonthlyCaseData({
  //   labels: data.map((item) => item.month),
  //   datasets: [
  //     {
  //       label: "Monthly Cases",
  //       data: data.map((item) => item.count),
  //       backgroundColor: ["#FF6384", "#36A2EB", "#FFCE56", "#4BC0C0", "#9966FF", "#FF9F40"],
  //     },
  //   ],
  // });

  const calculateTimeRemaining = (caseCloseDate, claimTypeName) => {
    if (!caseCloseDate) return 'N/A';
    
    try {
      const closeDate = new Date(caseCloseDate);
      closeDate.setHours(23, 59, 59, 999);
      
      const now = new Date();
      const diffMs = closeDate.getTime() - now.getTime();
      
      if (diffMs <= 0) {
        return 'Expired';
      }
      
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffDays = Math.floor(diffHours / 24);
      const remainingHours = diffHours % 24;
      const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
      
      if (claimTypeName && claimTypeName.toLowerCase() === 'cashless') {
        if (diffHours > 0) {
          return `${diffHours} hour${diffHours > 1 ? 's' : ''} ${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} left`;
        } else {
          return `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} left`;
        }
      }
      
      if (diffDays > 0) {
        if (remainingHours > 0) {
          return `${diffDays} day${diffDays > 1 ? 's' : ''} ${remainingHours} hour${remainingHours > 1 ? 's' : ''} left`;
        } else {
          return `${diffDays} day${diffDays > 1 ? 's' : ''} left`;
        }
      } else {
        return `${remainingHours} hour${remainingHours > 1 ? 's' : ''} left`;
      }
    } catch (error) {
      console.error('Error calculating time remaining:', error);
      return 'N/A';
    }
  };

  return (
    <>
      <PageTitle
        breadCrumbItems={[
          { label: "Dashboard", path: "/", active: true },
        ]}
        title={"Dashboard"}
      />


      <div className="dashboard-compact">
        <Row className="g-3 dashboard-stats-row">
          {permittedMenuChildren.map((child, index) => {
            const statusName = keyToStatus[child.key] || child.label;
            const variant = widgetVariants[index % widgetVariants.length];
            const icon = widgetIcons[index % widgetIcons.length];
            const stats = getStatusCount(statusName);
            const description = statusName;
            return (
              <Col key={child.key || index} md={6} lg={4} xl={2}>
                <div
                  className={`modern-stat-wrapper modern-stat-${variant}`}
                  style={{ height: "100%", cursor: "pointer" }}
                  role="link"
                  tabIndex={0}
                  onClick={() => navigate(child.url || "/")}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      navigate(child.url || "/");
                    }
                  }}
                >
                  <StatisticsWidget
                    variant={variant}
                    counterOptions={{ prefix: "" }}
                    description={description}
                    stats={stats}
                    icon={icon}
                  />
                </div>
              </Col>
            );
          })}
        </Row>
      </div>

      <Row className="g-4" style={{ marginTop: "2px" }}>
        <Col lg={8}>
          <Card
            className="p-4"
            style={dashboardStyles.card}
            onMouseEnter={(e) => {
              Object.assign(e.currentTarget.style, dashboardStyles.cardHover);
            }}
            onMouseLeave={(e) => {
              Object.assign(e.currentTarget.style, dashboardStyles.card);
            }}
          >
            <div style={dashboardStyles.cardHeader}>
              <i className="fe-alert-circle text-danger me-2"></i>
              Today Expiry Active Case
            </div>
            <div style={{ position: "relative", ...dashboardStyles.tableContainer }}>
              {/* Fixed Header */}
              <div ref={expiryHeaderRef} style={{ position: "absolute", top: 0, left: 0, right: 0, zIndex: 100, backgroundColor: "#fff", overflow: "hidden" }}>
                <Table className="mb-0 compact-table" style={{ marginBottom: 0, tableLayout: "fixed", width: "100%", boxSizing: "border-box" }}>
                  <thead>
                    <tr>
                      <th style={{ ...dashboardStyles.tableHeader, width: "18%", padding: "16px" }}>Expiry Date</th>
                      <th style={{ ...dashboardStyles.tableHeader, width: "16%", padding: "16px" }}>Claim Number</th>
                      <th style={{ ...dashboardStyles.tableHeader, width: "24%", padding: "16px" }}>Insurance Company</th>
                      <th style={{ ...dashboardStyles.tableHeader, width: "24%", padding: "16px" }}>Hospital</th>
                      <th style={{ ...dashboardStyles.tableHeader, width: "20%", padding: "16px" }}>Manager Name</th>
                    </tr>
                  </thead>
                </Table>
              </div>
              {/* Scrollable Body */}
              <div
                ref={expiryBodyRef}
                className="dashboard-table-scroll"
                style={{
                  maxHeight: "387px",
                  overflowY: "auto",
                  marginTop: "72px",
                  scrollbarGutter: "stable"
                }}
              >
                <Table className="mb-0 compact-table" style={{ tableLayout: "fixed", width: "100%", boxSizing: "border-box" }}>
                  <colgroup>
                    <col style={{ width: "16%" }} />
                    <col style={{ width: "16%" }} />
                    <col style={{ width: "24%" }} />
                    <col style={{ width: "24%" }} />
                    <col style={{ width: "20%" }} />
                  </colgroup>
                  <tbody>
                    {dashboardData?.expiredCase && dashboardData.expiredCase.length > 0 ? (
                      dashboardData.expiredCase.map((value, index) => {
                        const caseId = value?.case_id ?? value?.id;
                        return (
                        <tr
                          key={index}
                          style={{ ...dashboardStyles.tableRow, cursor: caseId ? "pointer" : "default" }}
                          onClick={() => {
                            if (caseId) {
                              navigate(`/Case-Summary/${caseId}`);
                            }
                          }}
                          onMouseEnter={(e) => {
                            Object.assign(
                              e.currentTarget.style,
                              dashboardStyles.tableRowHover,
                              { cursor: caseId ? "pointer" : "default" }
                            );
                          }}
                          onMouseLeave={(e) => {
                            Object.assign(
                              e.currentTarget.style,
                              dashboardStyles.tableRow,
                              { cursor: caseId ? "pointer" : "default" }
                            );
                          }}
                        >
                          <td className="text-center" style={{ width: "18%", padding: "14px" }}>
                            <Badge bg="danger" style={dashboardStyles.badge}>
                              {calculateTimeRemaining(value.case_close_date, value.claimTypeCaseDetails?.name)}
                            </Badge>
                          </td>
                          <td style={{ width: "16%", padding: "14px", fontWeight: "500", color: "#495057" }}>{value.claim_number || 'N/A'}</td>
                          <td style={{ width: "24%", padding: "14px", color: "#6c757d" }}>{value.insuranceIdCaseDetails?.name || 'N/A'}</td>
                          <td style={{ width: "24%", padding: "14px", color: "#6c757d" }}>{value.hospitaldCaseDetails?.hospital_name || 'N/A'}</td>
                          <td style={{ width: "20%", padding: "14px", color: "#495057" }}>{value.ManagerIdCaseDetails?.name || 'N/A'}</td>
                        </tr>
                      )})
                    ) : (
                      <tr>
                        <td colSpan="5" className="text-center" style={{ padding: "20px" }}>No data available</td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </div>
          </Card>
        </Col>

        <Col lg={4}>
          <Card
            className="p-4"
            style={dashboardStyles.card}
            onMouseEnter={(e) => {
              Object.assign(e.currentTarget.style, dashboardStyles.cardHover);
            }}
            onMouseLeave={(e) => {
              Object.assign(e.currentTarget.style, dashboardStyles.card);
            }}
          >
            <div style={dashboardStyles.cardHeader}>
              <i className="fe-pie-chart text-primary me-2"></i>
              Monthly Case
            </div>
            <div className="d-flex flex-column align-items-center">
              {/* Modern Legend with Statistics */}
              <div className="d-flex align-items-center justify-content-around mb-4" style={{ gap: "30px", width: "100%" }}>
                <div
                  role="button"
                  tabIndex={0}
                  className="d-flex flex-column align-items-center"
                  style={{
                    padding: "12px 16px",
                    borderRadius: "12px",
                    background: "linear-gradient(135deg, rgba(255, 99, 132, 0.1) 0%, rgba(255, 99, 132, 0.05) 100%)",
                    border: "1px solid rgba(255, 99, 132, 0.2)",
                    minWidth: "140px",
                    transition: "all 0.3s ease",
                    cursor: "pointer"
                  }}
                  onClick={() => handleMonthlyCardClick("Active")}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleMonthlyCardClick("Active"); } }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow = "0 4px 12px rgba(255, 99, 132, 0.2)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "none";
                  }}
                >
                  <div className="d-flex align-items-center mb-2">
                    <span style={{
                      background: "linear-gradient(135deg, #ff6b9d 0%, #c44569 100%)",
                      width: 18,
                      height: 18,
                      display: "inline-block",
                      marginRight: 10,
                      borderRadius: "6px",
                      boxShadow: "0 3px 8px rgba(255, 99, 132, 0.4)"
                    }}></span>
                    <span style={{ fontWeight: "600", color: "#2c3e50", fontSize: "13px", letterSpacing: "0.3px" }}>Active Cases</span>
                  </div>
                  <div style={{ fontSize: "20px", fontWeight: "700", color: "#ff6b9d", letterSpacing: "-0.5px" }}>
                    {Math.round((monthlyCaseData.datasets[0].data[0] / (monthlyCaseData.datasets[0].data[0] + monthlyCaseData.datasets[0].data[1])) * 100)}%
                  </div>
                  <div style={{ fontSize: "12px", color: "#64748b", marginTop: "2px" }}>
                    {monthlyCaseData.datasets[0].data[0]} cases
                  </div>
                </div>
                <div
                  role="button"
                  tabIndex={0}
                  className="d-flex flex-column align-items-center"
                  style={{
                    padding: "12px 16px",
                    borderRadius: "12px",
                    background: "linear-gradient(135deg, rgba(54, 162, 235, 0.1) 0%, rgba(54, 162, 235, 0.05) 100%)",
                    border: "1px solid rgba(54, 162, 235, 0.2)",
                    minWidth: "140px",
                    transition: "all 0.3s ease",
                    cursor: "pointer"
                  }}
                  onClick={() => handleMonthlyCardClick("Complete")}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleMonthlyCardClick("Complete"); } }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow = "0 4px 12px rgba(54, 162, 235, 0.2)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "none";
                  }}
                >
                  <div className="d-flex align-items-center mb-2">
                    <span style={{
                      background: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
                      width: 18,
                      height: 18,
                      display: "inline-block",
                      marginRight: 10,
                      borderRadius: "6px",
                      boxShadow: "0 3px 8px rgba(54, 162, 235, 0.4)"
                    }}></span>
                    <span style={{ fontWeight: "600", color: "#2c3e50", fontSize: "13px", letterSpacing: "0.3px" }}>Completed Cases</span>
                  </div>
                  <div style={{ fontSize: "20px", fontWeight: "700", color: "#4facfe", letterSpacing: "-0.5px" }}>
                    {Math.round((monthlyCaseData.datasets[0].data[1] / (monthlyCaseData.datasets[0].data[0] + monthlyCaseData.datasets[0].data[1])) * 100)}%
                  </div>
                  <div style={{ fontSize: "12px", color: "#64748b", marginTop: "2px" }}>
                    {monthlyCaseData.datasets[0].data[1]} cases
                  </div>
                </div>
              </div>

              {/* Modern Pie Chart */}
              <div style={{ height: "300px", display: "flex", justifyContent: "center", alignItems: "center", width: "100%", position: "relative" }}>
                <Pie
                  data={monthlyCaseData}
                  options={{
                    maintainAspectRatio: false,
                    animation: {
                      animateRotate: true,
                      animateScale: true,
                      duration: 1000,
                      easing: "easeOutQuart"
                    },
                    plugins: {
                      legend: { display: false },
                      tooltip: {
                        backgroundColor: "rgba(26, 32, 44, 0.95)",
                        padding: 16,
                        cornerRadius: 12,
                        displayColors: true,
                        titleFont: { size: 14, weight: "600", family: "'Inter', sans-serif" },
                        bodyFont: { size: 14, weight: "500", family: "'Inter', sans-serif" },
                        titleColor: "#ffffff",
                        bodyColor: "#e2e8f0",
                        borderColor: "rgba(255, 255, 255, 0.1)",
                        borderWidth: 1,
                        callbacks: {
                          label: function (context) {
                            const label = context.label || '';
                            const value = context.parsed || 0;
                            const total = context.dataset.data.reduce((a, b) => a + b, 0);
                            const percentage = ((value / total) * 100).toFixed(1);
                            return `${label}: ${value} (${percentage}%)`;
                          }
                        }
                      }
                    },
                    elements: {
                      arc: {
                        borderWidth: 3,
                        borderColor: "#ffffff",
                        hoverBorderWidth: 4,
                        hoverBorderColor: "#f8f9fa"
                      }
                    },
                    cutout: "0%"
                  }}
                />
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Additional Charts Row */}
      <Row className="g-4">
        <Col lg={8}>
          <Card
            className="p-4"
            style={dashboardStyles.card}
            onMouseEnter={(e) => { Object.assign(e.currentTarget.style, dashboardStyles.cardHover); }}
            onMouseLeave={(e) => { Object.assign(e.currentTarget.style, dashboardStyles.card); }}
          >
            <div style={dashboardStyles.cardHeader}>
              <i className="fe-bar-chart-line- text-primary me-2"></i>
              Case Status Distribution
            </div>
            <div style={{ height: "320px" }}>
              <Bar data={statusBarData} options={statusBarOptions} />
            </div>
          </Card>
        </Col>

        <Col lg={4}>
          <Card
            className="p-4"
            style={dashboardStyles.card}
            onMouseEnter={(e) => { Object.assign(e.currentTarget.style, dashboardStyles.cardHover); }}
            onMouseLeave={(e) => { Object.assign(e.currentTarget.style, dashboardStyles.card); }}
          >
            <div style={dashboardStyles.cardHeader}>
              <i className="fe-sliders text-info me-2"></i>
              Claim Type Split
            </div>
            <div style={{ height: "320px" }}>
              <Doughnut
                ref={claimTypeChartRef}
                data={claimTypeData}
                options={{
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: "bottom",
                      onClick: (_event, legendItem) => {
                        // Legend label click -> open modal with that claim type cases
                        openClaimTypeCasesByIndex(legendItem.index);
                      },
                    },
                  },
                  cutout: "55%",
                }}
                onClick={(event) => {
                  if (!claimTypeChartRef.current) return;
                  const elements = claimTypeChartRef.current.getElementsAtEventForMode(
                    event,
                    "nearest",
                    { intersect: true },
                    false
                  );
                  handleClaimTypeChartClick(elements);
                }}
              />
            </div>
          </Card>
        </Col>
      </Row>

      <Row className="g-4">
        <Col lg={12}>
          <Card
            className="p-4"
            style={dashboardStyles.card}
            onMouseEnter={(e) => { Object.assign(e.currentTarget.style, dashboardStyles.cardHover); }}
            onMouseLeave={(e) => { Object.assign(e.currentTarget.style, dashboardStyles.card); }}
          >
            <div style={dashboardStyles.cardHeader}>
              <i className="fe-map-pin text-success me-2"></i>
              City-wise Active Cases
            </div>
            <div style={{ height: "340px" }}>
              <Bar data={cityBarData} options={cityBarOptions} />
            </div>
          </Card>
        </Col>
      </Row>

      <Row className="g-4">
        <Col lg={4}>
          <Card
            className="p-4"
            style={dashboardStyles.card}
            onMouseEnter={(e) => {
              Object.assign(e.currentTarget.style, dashboardStyles.cardHover);
            }}
            onMouseLeave={(e) => {
              Object.assign(e.currentTarget.style, dashboardStyles.card);
            }}
          >
            <div style={dashboardStyles.cardHeader}>
              <i className="fe-users text-info me-2"></i>
              FO Wise Active Case
            </div>
            <div style={{ position: "relative", ...dashboardStyles.tableContainer }}>
              {/* Fixed Header */}
              <div ref={foHeaderRef} style={{ position: "absolute", top: 0, left: 0, right: 0, zIndex: 100, backgroundColor: "#fff", overflow: "hidden" }}>
                <Table className="mb-0 compact-table" style={{ marginBottom: 0, tableLayout: "fixed", width: "100%", boxSizing: "border-box" }}>
                  <thead>
                    <tr>
                      <th style={{ ...dashboardStyles.tableHeader, width: "65%", padding: "16px" }}>Name</th>
                      <th style={{ ...dashboardStyles.tableHeader, width: "35%", padding: "16px", textAlign: "center" }}>Total</th>
                    </tr>
                  </thead>
                </Table>
              </div>
              {/* Scrollable Body */}
              <div
                ref={foBodyRef}
                className="dashboard-table-scroll"
                style={{
                  maxHeight: "280px",
                  overflowY: "auto",
                  marginTop: "56px",
                  scrollbarGutter: "stable"
                }}
              >
                <Table className="mb-0 compact-table" style={{ tableLayout: "fixed", width: "100%", boxSizing: "border-box" }}>
                  <colgroup>
                    <col style={{ width: "65%" }} />
                    <col style={{ width: "35%" }} />
                  </colgroup>
                  <tbody>
                    {dashboardData?.fieldOfficerWiseCount && dashboardData.fieldOfficerWiseCount.length > 0 ? (
                      dashboardData.fieldOfficerWiseCount.map((value, index) => {
                        const foId = value?.id ?? value?.user_id ?? value?.field_officer_id;
                        return (
                        <tr
                          key={index}
                          style={{ ...dashboardStyles.tableRow, cursor: foId != null ? "pointer" : "default" }}
                          onClick={() => foId != null && openDashboardSearchByType("fo_wise", foId, value.field_officer_name || "FO")}
                          onMouseEnter={(e) => {
                            Object.assign(e.currentTarget.style, dashboardStyles.tableRowHover, foId != null ? { cursor: "pointer" } : {});
                          }}
                          onMouseLeave={(e) => {
                            Object.assign(e.currentTarget.style, dashboardStyles.tableRow, { cursor: foId != null ? "pointer" : "default" });
                          }}
                        >
                          <td style={{ width: "65%", padding: "14px", fontWeight: "500", color: "#495057" }}>{value.field_officer_name || 'N/A'}</td>
                          <td className="text-center" style={{ width: "35%", padding: "14px" }}>
                            <Badge bg="success" style={dashboardStyles.badge}>{value.total || 0}</Badge>
                          </td>
                        </tr>
                      );})
                    ) : (
                      <tr>
                        <td colSpan="2" className="text-center" style={{ padding: "20px" }}>No data available</td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </div>
          </Card>
        </Col>

        <Col lg={4}>
          <Card
            className="p-4"
            style={dashboardStyles.card}
            onMouseEnter={(e) => {
              Object.assign(e.currentTarget.style, dashboardStyles.cardHover);
            }}
            onMouseLeave={(e) => {
              Object.assign(e.currentTarget.style, dashboardStyles.card);
            }}
          >
            <div style={dashboardStyles.cardHeader}>
              <i className="fe-map-pin text-success me-2"></i>
              City Wise Active Case
            </div>
            <div style={{ position: "relative", ...dashboardStyles.tableContainer }}>
              {/* Fixed Header */}
              <div ref={cityHeaderRef} style={{ position: "absolute", top: 0, left: 0, right: 0, zIndex: 100, backgroundColor: "#fff", overflow: "hidden" }}>
                <Table className="mb-0 compact-table" style={{ marginBottom: 0, tableLayout: "fixed", width: "100%", boxSizing: "border-box" }}>
                  <thead>
                    <tr>
                      <th style={{ ...dashboardStyles.tableHeader, width: "65%", padding: "16px" }}>City</th>
                      <th style={{ ...dashboardStyles.tableHeader, width: "35%", padding: "16px", textAlign: "center" }}>Total</th>
                    </tr>
                  </thead>
                </Table>
              </div>
              {/* Scrollable Body */}
              <div
                ref={cityBodyRef}
                className="dashboard-table-scroll"
                style={{
                  maxHeight: "280px",
                  overflowY: "auto",
                  marginTop: "56px",
                  scrollbarGutter: "stable"
                }}
              >
                <Table className="mb-0 compact-table" style={{ tableLayout: "fixed", width: "100%", boxSizing: "border-box" }}>
                  <colgroup>
                    <col style={{ width: "65%" }} />
                    <col style={{ width: "35%" }} />
                  </colgroup>
                  <tbody>
                    {dashboardData?.cityCount && dashboardData.cityCount.length > 0 ? (
                      dashboardData.cityCount.map((value, index) => {
                        const cityId = value?.id ?? value?.city_id ?? value?.city_code;
                        return (
                        <tr
                          key={index}
                          style={{ ...dashboardStyles.tableRow, cursor: cityId != null && cityId !== "" ? "pointer" : "default" }}
                          onClick={() => (cityId != null && cityId !== "") && openDashboardSearchByType("city_wise", cityId, value.city_name || "City")}
                          onMouseEnter={(e) => {
                            Object.assign(e.currentTarget.style, dashboardStyles.tableRowHover, (cityId != null && cityId !== "") ? { cursor: "pointer" } : {});
                          }}
                          onMouseLeave={(e) => {
                            Object.assign(e.currentTarget.style, dashboardStyles.tableRow, { cursor: (cityId != null && cityId !== "") ? "pointer" : "default" });
                          }}
                        >
                          <td style={{ width: "65%", padding: "14px", fontWeight: "500", color: "#495057" }}>{value.city_name || 'N/A'}</td>
                          <td className="text-center" style={{ width: "35%", padding: "14px" }}>
                            <Badge bg="success" style={dashboardStyles.badge}>{value.total || 0}</Badge>
                          </td>
                        </tr>
                      );})
                    ) : (
                      <tr>
                        <td colSpan="2" className="text-center" style={{ padding: "20px" }}>No data available</td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </div>
          </Card>
        </Col>

        <Col lg={4}>
          <Card
            className="p-4"
            style={dashboardStyles.card}
            onMouseEnter={(e) => {
              Object.assign(e.currentTarget.style, dashboardStyles.cardHover);
            }}
            onMouseLeave={(e) => {
              Object.assign(e.currentTarget.style, dashboardStyles.card);
            }}
          >
            <div style={dashboardStyles.cardHeader}>
              <i className="fe-home text-warning me-2"></i>
              Hospital Wise Active Case
            </div>
            <div style={{ position: "relative", ...dashboardStyles.tableContainer }}>
              {/* Fixed Header */}
              <div ref={hospitalHeaderRef} style={{ position: "absolute", top: 0, left: 0, right: 0, zIndex: 100, backgroundColor: "#fff", overflow: "hidden" }}>
                <Table className="mb-0 compact-table" style={{ marginBottom: 0, tableLayout: "fixed", width: "100%", boxSizing: "border-box" }}>
                  <thead>
                    <tr>
                      <th style={{ ...dashboardStyles.tableHeader, width: "65%", padding: "16px" }}>Hospital</th>
                      <th style={{ ...dashboardStyles.tableHeader, width: "35%", padding: "16px", textAlign: "center" }}>Total</th>
                    </tr>
                  </thead>
                </Table>
              </div>
              {/* Scrollable Body */}
              <div
                ref={hospitalBodyRef}
                className="dashboard-table-scroll"
                style={{
                  maxHeight: "280px",
                  overflowY: "auto",
                  marginTop: "56px",
                  scrollbarGutter: "stable"
                }}
              >
                <Table className="mb-0 compact-table" style={{ tableLayout: "fixed", width: "100%", boxSizing: "border-box" }}>
                  <colgroup>
                    <col style={{ width: "65%" }} />
                    <col style={{ width: "35%" }} />
                  </colgroup>
                  <tbody>
                    {dashboardData?.hospitalCount && dashboardData.hospitalCount.length > 0 ? (
                      dashboardData.hospitalCount.map((value, index) => {
                        const hospitalId = value?.id ?? value?.hospital_id;
                        return (
                        <tr
                          key={index}
                          style={{ ...dashboardStyles.tableRow, cursor: hospitalId != null ? "pointer" : "default" }}
                          onClick={() => hospitalId != null && openDashboardSearchByType("hospital_wise", hospitalId, value.hospital_name || "Hospital")}
                          onMouseEnter={(e) => {
                            Object.assign(e.currentTarget.style, dashboardStyles.tableRowHover, hospitalId != null ? { cursor: "pointer" } : {});
                          }}
                          onMouseLeave={(e) => {
                            Object.assign(e.currentTarget.style, dashboardStyles.tableRow, { cursor: hospitalId != null ? "pointer" : "default" });
                          }}
                        >
                          <td style={{ width: "65%", padding: "14px", fontWeight: "500", color: "#495057" }}>{value.hospital_name || 'N/A'}</td>
                          <td className="text-center" style={{ width: "35%", padding: "14px" }}>
                            <Badge bg="success" style={dashboardStyles.badge}>{value.total || 0}</Badge>
                          </td>
                        </tr>
                      );})
                    ) : (
                      <tr>
                        <td colSpan="2" className="text-center" style={{ padding: "20px" }}>No data available</td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      <Row className="g-4">
        <Col lg={4}>
          <Card
            className="p-4"
            style={dashboardStyles.card}
            onMouseEnter={(e) => {
              Object.assign(e.currentTarget.style, dashboardStyles.cardHover);
            }}
            onMouseLeave={(e) => {
              Object.assign(e.currentTarget.style, dashboardStyles.card);
            }}
          >
            <div style={dashboardStyles.cardHeader}>
              <i className="fe-shield text-primary me-2"></i>
              Insurance Wise Active Case
            </div>
            <div style={{ position: "relative", ...dashboardStyles.tableContainer }}>
              {/* Fixed Header */}
              <div ref={insuranceHeaderRef} style={{ position: "absolute", top: 0, left: 0, right: 0, zIndex: 100, backgroundColor: "#fff", overflow: "hidden" }}>
                <Table className="mb-0 compact-table" style={{ marginBottom: 0, tableLayout: "fixed", width: "100%", boxSizing: "border-box" }}>
                  <thead>
                    <tr>
                      <th style={{ ...dashboardStyles.tableHeader, width: "50%", padding: "16px" }}>Insurance Company</th>
                      <th style={{ ...dashboardStyles.tableHeader, width: "30%", padding: "16px" }}>Claim Type</th>
                      <th style={{ ...dashboardStyles.tableHeader, width: "20%", padding: "16px", textAlign: "center" }}>Total</th>
                    </tr>
                  </thead>
                </Table>
              </div>
              {/* Scrollable Body */}
              <div
                ref={insuranceBodyRef}
                className="dashboard-table-scroll"
                style={{
                  maxHeight: "280px",
                  overflowY: "auto",
                  marginTop: "56px",
                  scrollbarGutter: "stable"
                }}
              >
                <Table className="mb-0 compact-table" style={{ tableLayout: "fixed", width: "100%", boxSizing: "border-box" }}>
                  <colgroup>
                    <col style={{ width: "50%" }} />
                    <col style={{ width: "30%" }} />
                    <col style={{ width: "20%" }} />
                  </colgroup>
                  <tbody>
                    {dashboardData?.insuranceCount && dashboardData.insuranceCount.length > 0 ? (
                      dashboardData.insuranceCount.map((value, index) => {
                        const insuranceId = value?.id ?? value?.insurance_id;
                        const claimTypeId = value?.claim_type_id ?? value?.claimTypeId;
                        const title = [value.insurance_name, value.claim_type_name].filter(Boolean).join(" - ") || "Insurance";
                        const hasValidIds = insuranceId != null && insuranceId !== "" && claimTypeId != null && claimTypeId !== "";
                        return (
                        <tr
                          key={index}
                          style={{ ...dashboardStyles.tableRow, cursor: hasValidIds ? "pointer" : "default" }}
                          onClick={() => hasValidIds && openDashboardSearchByType("insurance_wise", insuranceId, title, claimTypeId)}
                          onMouseEnter={(e) => {
                            Object.assign(e.currentTarget.style, dashboardStyles.tableRowHover, hasValidIds ? { cursor: "pointer" } : {});
                          }}
                          onMouseLeave={(e) => {
                            Object.assign(e.currentTarget.style, dashboardStyles.tableRow, { cursor: hasValidIds ? "pointer" : "default" });
                          }}
                        >
                          <td style={{ width: "50%", padding: "14px", fontWeight: "500", color: "#495057" }}>{value.insurance_name || 'N/A'}</td>
                          <td style={{ width: "30%", padding: "14px", color: "#6c757d" }}>{value.claim_type_name || 'N/A'}</td>
                          <td className="text-center" style={{ width: "20%", padding: "14px" }}>
                            <Badge bg="success" style={dashboardStyles.badge}>{value.total || 0}</Badge>
                          </td>
                        </tr>
                      );})
                    ) : (
                      <tr>
                        <td colSpan="3" className="text-center" style={{ padding: "20px" }}>No data available</td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </div>
          </Card>
        </Col>

        <Col lg={8}>
          <Card
            className="p-4"
            style={dashboardStyles.card}
            onMouseEnter={(e) => {
              Object.assign(e.currentTarget.style, dashboardStyles.cardHover);
            }}
            onMouseLeave={(e) => {
              Object.assign(e.currentTarget.style, dashboardStyles.card);
            }}
          >
            <div style={dashboardStyles.cardHeader}>
              <i className="fe-check-circle text-success me-2"></i>
              Today Completed Case
            </div>
            <div style={{ position: "relative", ...dashboardStyles.tableContainer }}>
              {/* Fixed Header */}
              <div ref={completedHeaderRef} style={{ position: "absolute", top: 0, left: 0, right: 0, zIndex: 100, backgroundColor: "#fff", overflow: "hidden" }}>
                <Table className="mb-0 compact-table" style={{ marginBottom: 0, tableLayout: "fixed", width: "100%", boxSizing: "border-box" }}>
                  <thead>
                    <tr>
                      <th style={{ ...dashboardStyles.tableHeader, width: "18%", padding: "16px" }}>Claim Number</th>
                      <th style={{ ...dashboardStyles.tableHeader, width: "18%", padding: "16px" }}>Policy Number</th>
                      <th style={{ ...dashboardStyles.tableHeader, width: "23%", padding: "16px" }}>Insurance Company</th>
                      <th style={{ ...dashboardStyles.tableHeader, width: "23%", padding: "16px" }}>Hospital</th>
                      <th style={{ ...dashboardStyles.tableHeader, width: "18%", padding: "16px" }}>Manager Name</th>
                    </tr>
                  </thead>
                </Table>
              </div>
              {/* Scrollable Body */}
              <div
                ref={completedBodyRef}
                className="dashboard-table-scroll"
                style={{
                  maxHeight: "280px",
                  overflowY: "auto",
                  marginTop: "56px",
                  scrollbarGutter: "stable"
                }}
              >
                <Table className="mb-0 compact-table" style={{ tableLayout: "fixed", width: "100%", boxSizing: "border-box" }}>
                  <colgroup>
                    <col style={{ width: "18%" }} />
                    <col style={{ width: "18%" }} />
                    <col style={{ width: "23%" }} />
                    <col style={{ width: "23%" }} />
                    <col style={{ width: "18%" }} />
                  </colgroup>
                  <tbody>
                    {dashboardData?.expiredCase && dashboardData.expiredCase.length > 0 ? (
                      dashboardData.expiredCase
                        .filter(value => value.case_status === "Case Closed")
                        .map((value, index) => (
                          <tr
                            key={index}
                            style={dashboardStyles.tableRow}
                            onMouseEnter={(e) => {
                              Object.assign(e.currentTarget.style, dashboardStyles.tableRowHover);
                            }}
                            onMouseLeave={(e) => {
                              Object.assign(e.currentTarget.style, dashboardStyles.tableRow);
                            }}
                          >
                            <td style={{ width: "18%", padding: "14px", fontWeight: "500", color: "#495057" }}>{value.claim_number || 'N/A'}</td>
                            <td style={{ width: "18%", padding: "14px", color: "#6c757d" }}>{value.policy_number || 'N/A'}</td>
                            <td style={{ width: "23%", padding: "14px", color: "#6c757d" }}>{value.insuranceIdCaseDetails?.name || 'N/A'}</td>
                            <td style={{ width: "23%", padding: "14px", color: "#6c757d" }}>{value.hospitaldCaseDetails?.hospital_name || 'N/A'}</td>
                            <td style={{ width: "18%", padding: "14px", color: "#495057" }}>{value.ManagerIdCaseDetails?.name || 'N/A'}</td>
                          </tr>
                        ))
                    ) : (
                      <tr>
                        <td colSpan="5" className="text-center" style={{ padding: "20px" }}>No data available</td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Monthly Report Cases Modal */}
      <Modal
        show={showMonthlyModal}
        onHide={() => setShowMonthlyModal(false)}
        size="xl"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {searchContext === "monthly"
              ? `${monthlyReportType === "Active" ? "Active Cases" : "Completed Cases"} (Monthly)`
              : searchContext === "claimType"
                ? `${selectedClaimTypeName || "Claim Type"} Cases`
                : `${searchModalTitle || searchContext} - Cases`}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
          {loadingMonthlyReport ? (
            <div className="d-flex justify-content-center align-items-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : (
            <Table responsive bordered hover size="sm">
              <thead style={dashboardStyles.tableHeader}>
                <tr>
                  <th>Claim Number</th>
                  <th>Policy Number</th>
                  <th>Insurance Company</th>
                  <th>Hospital</th>
                  <th>Manager Name</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {monthlyReportList && monthlyReportList.length > 0 ? (
                  monthlyReportList.map((item, index) => {
                    const caseId = item?.case_id ?? item?.id;
                    return (
                      <tr
                        key={index}
                        style={{ ...dashboardStyles.tableRow, cursor: caseId ? "pointer" : "default" }}
                        onClick={() => {
                          if (caseId) navigate(`/Case-Summary/${caseId}`);
                        }}
                        onMouseEnter={(e) => {
                          if (caseId) Object.assign(e.currentTarget.style, dashboardStyles.tableRowHover);
                        }}
                        onMouseLeave={(e) => {
                          Object.assign(e.currentTarget.style, dashboardStyles.tableRow);
                        }}
                      >
                        <td>{item?.claim_number || "N/A"}</td>
                        <td>{item?.policy_number || "N/A"}</td>
                        <td>{item?.insuranceIdCaseDetails?.name || "N/A"}</td>
                        <td>{item?.hospitaldCaseDetails?.hospital_name || "N/A"}</td>
                        <td>{item?.ManagerIdCaseDetails?.name || "N/A"}</td>
                        <td>{item?.case_status || "N/A"}</td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="6" className="text-center py-4">No cases found</td>
                  </tr>
                )}
              </tbody>
            </Table>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowMonthlyModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default Dashboard;